import { DEFAULT_SETTINGS } from "@/lib/constants";
import { StorageType } from "@/lib/file-operations.lib";
import { CanvasSettings } from "@/types/diagram";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

type PanelType = "diagrams" | "shapes" | "composition" | "chat";

interface UIContextType {
  // Panel and layout state
  activePanel: PanelType;
  leftSidebarOpen: boolean;
  rightSidebarOpen: boolean;
  fullScreenPanel: boolean;

  // Dialog states
  isSaveDiagramDialogOpen: boolean;
  isSaveComponentDialogOpen: boolean;
  isSettingsDialogOpen: boolean;
  isLibraryDialogOpen: boolean;
  isCreateDiagramOpen: boolean;
  isDropdownOpen: boolean;

  // Display settings
  showAttachmentPoints: boolean;
  canvasSettings: CanvasSettings | null;

  // File operations
  fileName: string;
  folderPath: string;
  storageType: StorageType;
  errorMessage: string | null;

  // Progress and loading states
  loadingProgress: {
    currentFile: string;
    loadedFiles: number;
    totalFiles: number;
  } | null;
  saveLoadMessage: string | null;
  isPending: boolean;
  showUpdateSuccess: boolean;
  autoSaveMode: boolean;

  // Actions for panels and layout
  setActivePanel: (panel: PanelType) => void;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;
  setRightSidebarOpen: (open: boolean) => void;
  setFullScreenPanel: (fullscreen: boolean) => void;

  // Dialog actions
  openSaveDiagramDialog: () => void;
  closeSaveDiagramDialog: () => void;
  openSaveComponentDialog: () => void;
  closeSaveComponentDialog: () => void;
  openSettingsDialog: () => void;
  closeSettingsDialog: () => void;
  openLibraryDialog: () => void;
  closeLibraryDialog: () => void;
  openCreateDiagramDialog: () => void;
  closeCreateDiagramDialog: () => void;
  setIsDropdownOpen: (open: boolean) => void;

  // Settings actions
  setShowAttachmentPoints: (show: boolean) => void;
  setCanvasSettings: (settings: CanvasSettings) => void;

  // File operations actions
  setFileName: (name: string) => void;
  setFolderPath: (path: string) => void;
  setStorageType: (type: StorageType) => void;
  setErrorMessage: (message: string | null) => void;

  // Progress and loading actions
  setLoadingProgress: (
    progress: {
      currentFile: string;
      loadedFiles: number;
      totalFiles: number;
    } | null
  ) => void;
  setSaveLoadMessage: (message: string | null) => void;
  setIsPending: (pending: boolean) => void;
  setShowUpdateSuccess: (show: boolean) => void;
  setAutoSaveMode: (mode: boolean) => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

interface UIProviderProps {
  children: ReactNode;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children }) => {
  // Panel and layout state
  const [activePanel, setActivePanel] = useState<PanelType>("chat");
  const [leftSidebarOpen, setLeftSidebarOpen] = useState<boolean>(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState<boolean>(false);
  const [fullScreenPanel, setFullScreenPanel] = useState<boolean>(false);

  // Dialog states
  const [isSaveDiagramDialogOpen, setIsSaveDiagramDialogOpen] =
    useState<boolean>(false);
  const [isSaveComponentDialogOpen, setIsSaveComponentDialogOpen] =
    useState<boolean>(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] =
    useState<boolean>(false);
  const [isLibraryDialogOpen, setIsLibraryDialogOpen] =
    useState<boolean>(false);
  const [isCreateDiagramOpen, setIsCreateDiagramOpen] =
    useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  // Display settings - Load from localStorage
  const [showAttachmentPoints, setShowAttachmentPointsState] =
    useState<boolean>(() => {
      return localStorage.getItem("showAttachmentPoints") === "true";
    });

  const [canvasSettings, setCanvasSettingsState] =
    useState<CanvasSettings | null>(() => {
      const savedSettings = localStorage.getItem("canvasSettings");
      const parsedSettings = savedSettings
        ? JSON.parse(savedSettings)
        : DEFAULT_SETTINGS;

      const settings = {
        ...parsedSettings,
        layerLabel: {
          ...parsedSettings.layerLabel,
          fontSize: Math.max(parsedSettings?.layerLabel?.fontSize ?? 60, 60),
        },
      };

      return settings;
    });

  // File operations - Load from localStorage
  const [fileName, setFileNameState] = useState<string>(() => {
    return localStorage.getItem("fileName") || "diagram.svg";
  });

  const [folderPath, setFolderPathState] = useState<string>(() => {
    return localStorage.getItem("folderPath") || "My Diagrams";
  });

  const [storageType, setStorageType] = useState<StorageType>(
    StorageType.Local
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Progress and loading states
  const [loadingProgress, setLoadingProgress] = useState<{
    currentFile: string;
    loadedFiles: number;
    totalFiles: number;
  } | null>(null);
  const [saveLoadMessage, setSaveLoadMessage] = useState<string | null>(null);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState<boolean>(false);
  const [autoSaveMode, setAutoSaveMode] = useState<boolean>(false);

  // Panel and layout actions
  const toggleLeftSidebar = useCallback(() => {
    setLeftSidebarOpen((prev) => !prev);
  }, []);

  const toggleRightSidebar = useCallback(() => {
    setRightSidebarOpen((prev) => !prev);
  }, []);

  // Dialog actions
  const openSaveDiagramDialog = useCallback(
    () => setIsSaveDiagramDialogOpen(true),
    []
  );
  const closeSaveDiagramDialog = useCallback(
    () => setIsSaveDiagramDialogOpen(false),
    []
  );
  const openSaveComponentDialog = useCallback(
    () => setIsSaveComponentDialogOpen(true),
    []
  );
  const closeSaveComponentDialog = useCallback(
    () => setIsSaveComponentDialogOpen(false),
    []
  );
  const openSettingsDialog = useCallback(
    () => setIsSettingsDialogOpen(true),
    []
  );
  const closeSettingsDialog = useCallback(
    () => setIsSettingsDialogOpen(false),
    []
  );
  const openLibraryDialog = useCallback(() => setIsLibraryDialogOpen(true), []);
  const closeLibraryDialog = useCallback(
    () => setIsLibraryDialogOpen(false),
    []
  );
  const openCreateDiagramDialog = useCallback(
    () => setIsCreateDiagramOpen(true),
    []
  );
  const closeCreateDiagramDialog = useCallback(
    () => setIsCreateDiagramOpen(false),
    []
  );

  // Settings actions with localStorage persistence
  const setShowAttachmentPoints = useCallback((show: boolean) => {
    setShowAttachmentPointsState(show);
    localStorage.setItem("showAttachmentPoints", show.toString());
  }, []);

  const setCanvasSettings = useCallback((settings: CanvasSettings) => {
    setCanvasSettingsState(settings);
    localStorage.setItem("canvasSettings", JSON.stringify(settings));
  }, []);

  // File operations actions with localStorage persistence
  const setFileName = useCallback((name: string) => {
    setFileNameState(name);
    localStorage.setItem("fileName", name);
  }, []);

  const setFolderPath = useCallback((path: string) => {
    setFolderPathState(path);
    localStorage.setItem("folderPath", path);
  }, []);

  const value: UIContextType = {
    // Panel and layout state
    activePanel,
    leftSidebarOpen,
    rightSidebarOpen,
    fullScreenPanel,

    // Dialog states
    isSaveDiagramDialogOpen,
    isSaveComponentDialogOpen,
    isSettingsDialogOpen,
    isLibraryDialogOpen,
    isCreateDiagramOpen,
    isDropdownOpen,

    // Display settings
    showAttachmentPoints,
    canvasSettings,

    // File operations
    fileName,
    folderPath,
    storageType,
    errorMessage,

    // Progress and loading states
    loadingProgress,
    saveLoadMessage,
    isPending,
    showUpdateSuccess,
    autoSaveMode,

    // Actions for panels and layout
    setActivePanel,
    toggleLeftSidebar,
    toggleRightSidebar,
    setRightSidebarOpen,
    setFullScreenPanel,

    // Dialog actions
    openSaveDiagramDialog,
    closeSaveDiagramDialog,
    openSaveComponentDialog,
    closeSaveComponentDialog,
    openSettingsDialog,
    closeSettingsDialog,
    openLibraryDialog,
    closeLibraryDialog,
    openCreateDiagramDialog,
    closeCreateDiagramDialog,
    setIsDropdownOpen,

    // Settings actions
    setShowAttachmentPoints,
    setCanvasSettings,

    // File operations actions
    setFileName,
    setFolderPath,
    setStorageType,
    setErrorMessage,

    // Progress and loading actions
    setLoadingProgress,
    setSaveLoadMessage,
    setIsPending,
    setShowUpdateSuccess,
    setAutoSaveMode,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error("useUI must be used within a UIProvider");
  }
  return context;
};
